import React from 'react';
import { Cookie } from 'lucide-react';
import SEO from '../components/SEO';

const Cookies = () => {
  return (
    <div className="min-h-screen pt-20 bg-black">
      <SEO title="Cookie Policy | PopCrown Official" description="PopCrown cookie policy. How we use cookies on popcrownofficial.com." keywords="PopCrown cookies, cookie policy" canonical="/cookies" />
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="blog-card p-8 md:p-12">
          <div className="flex items-center gap-4 mb-8">
            <Cookie className="w-12 h-12 text-[#E10600]" />
            <h1 className="text-4xl font-bold text-white">Cookie Policy</h1>
          </div>

          <p className="text-gray-400 mb-8">
            <strong>Effective Date:</strong> March 2024<br />
            <strong>Last Updated:</strong> March 2024
          </p>

          <div className="space-y-8 text-gray-300">
            <section>
              <h2 className="text-2xl font-bold text-[#E10600] mb-4">1. What Are Cookies?</h2>
              <p>
                Cookies are small text files that are stored on your device (computer, tablet, or mobile) when you visit a website. They help websites remember your preferences and improve your browsing experience.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#E10600] mb-4">2. How We Use Cookies</h2>
              <p className="mb-4">
                PopCrown Official Ltd uses cookies and similar tracking technologies to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Keep you signed in to your account</li>
                <li>Remember your preferences and settings</li>
                <li>Understand how you use our website and app</li>
                <li>Improve our services and user experience</li>
                <li>Deliver personalized content and advertisements</li>
                <li>Analyze website traffic and performance</li>
                <li>Prevent fraud and enhance security</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#E10600] mb-4">3. Types of Cookies We Use</h2>
              
              <div className="space-y-6">
                <div className="bg-[#1a1a1a] p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-white mb-3">3.1 Essential Cookies (Required)</h3>
                  <p className="mb-2">
                    These cookies are necessary for the website to function and cannot be disabled. They enable core functionality such as:
                  </p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Authentication and security</li>
                    <li>Session management</li>
                    <li>Load balancing</li>
                    <li>Cookie consent preferences</li>
                  </ul>
                </div>

                <div className="bg-[#1a1a1a] p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-white mb-3">3.2 Functional Cookies (Optional)</h3>
                  <p className="mb-2">
                    These cookies remember your choices and preferences to provide a more personalized experience:
                  </p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Language preferences</li>
                    <li>Display settings</li>
                    <li>Previously visited pages</li>
                    <li>Form auto-fill information</li>
                  </ul>
                </div>

                <div className="bg-[#1a1a1a] p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-white mb-3">3.3 Analytics Cookies (Optional)</h3>
                  <p className="mb-2">
                    These cookies help us understand how visitors interact with our website:
                  </p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Pages visited and time spent</li>
                    <li>Click patterns and navigation paths</li>
                    <li>Device and browser information</li>
                    <li>Performance metrics</li>
                  </ul>
                  <p className="mt-2 text-sm text-gray-400">
                    We use services like Google Analytics for this purpose.
                  </p>
                </div>

                <div className="bg-[#1a1a1a] p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-white mb-3">3.4 Marketing Cookies (Optional)</h3>
                  <p className="mb-2">
                    These cookies track your online activity to deliver relevant advertisements:
                  </p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Personalized ads based on interests</li>
                    <li>Retargeting campaigns</li>
                    <li>Social media integration</li>
                    <li>Conversion tracking</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#E10600] mb-4">4. Third-Party Cookies</h2>
              <p className="mb-4">
                We use third-party services that may set their own cookies:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Google Analytics:</strong> Website analytics and performance tracking</li>
                <li><strong>Social Media Platforms:</strong> Instagram, Facebook, TikTok integration</li>
                <li><strong>Payment Processors:</strong> Secure payment processing</li>
                <li><strong>Content Delivery Networks:</strong> Faster content loading</li>
              </ul>
              <p className="mt-4 text-sm text-gray-400">
                These third parties have their own privacy policies and cookie practices.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#E10600] mb-4">5. Cookie Duration</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Session Cookies</h3>
                  <p>Temporary cookies that expire when you close your browser.</p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Persistent Cookies</h3>
                  <p>Remain on your device for a set period (days, months, or years) to remember your preferences across visits.</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#E10600] mb-4">6. Managing Your Cookie Preferences</h2>
              
              <h3 className="text-xl font-semibold text-white mb-3">6.1 Cookie Consent Banner</h3>
              <p className="mb-4">
                When you first visit our website, you'll see a cookie consent banner where you can:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>Accept all cookies</li>
                <li>Decline non-essential cookies</li>
                <li>Manage individual cookie categories</li>
              </ul>

              <h3 className="text-xl font-semibold text-white mb-3">6.2 Browser Settings</h3>
              <p className="mb-4">
                You can control cookies through your browser settings:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Chrome:</strong> Settings → Privacy and security → Cookies</li>
                <li><strong>Firefox:</strong> Settings → Privacy & Security → Cookies</li>
                <li><strong>Safari:</strong> Preferences → Privacy → Cookies</li>
                <li><strong>Edge:</strong> Settings → Cookies and site permissions</li>
              </ul>
              <p className="mt-4 text-sm text-gray-400">
                Note: Blocking all cookies may affect website functionality and your user experience.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#E10600] mb-4">7. Do Not Track (DNT)</h2>
              <p>
                We respect Do Not Track browser signals. When DNT is enabled, we will not track your browsing activity for advertising purposes. However, some essential cookies may still be necessary for core functionality.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#E10600] mb-4">8. Mobile App Tracking</h2>
              <p className="mb-4">
                The PopCrown Social mobile app uses similar tracking technologies:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Device identifiers (IDFA for iOS, Advertising ID for Android)</li>
                <li>Analytics SDKs</li>
                <li>Crash reporting tools</li>
              </ul>
              <p className="mt-4">
                You can manage tracking preferences in your device settings:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li><strong>iOS:</strong> Settings → Privacy → Tracking</li>
                <li><strong>Android:</strong> Settings → Google → Ads → Opt out of Ads Personalization</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#E10600] mb-4">9. Updates to This Policy</h2>
              <p>
                We may update this Cookie Policy from time to time to reflect changes in technology or legal requirements. We will notify you of significant changes through our website or app.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#E10600] mb-4">10. Contact Us</h2>
              <p className="mb-4">
                If you have questions about our use of cookies, please contact us:
              </p>
              <div className="bg-[#1a1a1a] p-4 rounded-lg">
                <p><strong className="text-white">PopCrown Official Ltd</strong></p>
                <p>Email: <a href="mailto:support@popcrownofficial.com" className="text-[#E10600] hover:underline">support@popcrownofficial.com</a></p>
              </div>
            </section>

            <section className="border-t border-gray-700 pt-6 mt-8">
              <p className="text-sm text-gray-400">
                By using PopCrown Official website and PopCrown Social app, you consent to the use of cookies as described in this Cookie Policy.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cookies;